// event-timeline — virtualized event log for a workflow run.
// Uses @tanstack/react-virtual for 60fps rendering with 1000+ events.
// token_delta events are excluded from the row list — they roll up into the
// parent step's liveTokens buffer instead.

import { useRef, useState, useEffect, useCallback } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Search } from "lucide-react";
import { useTranslations } from "use-intl";
import { cn } from "@/utils";
import { EventRow } from "./event-row";
import { SuggestBanner } from "@/components/ui/suggest-banner/suggest-banner";
import { Wifi } from "lucide-react";
import type { RunEvent } from "@/types/workflow.types";

// ── Filter taxonomy ───────────────────────────────────────────────────────────

type FilterChip = "all" | "llm" | "tool" | "decision" | "checkpoint" | "lifecycle" | "errors";

function normalizedEventType(eventType: string): string {
  return eventType.replace(/\./g, "_");
}

function matchesChip(event: RunEvent, chip: FilterChip): boolean {
  const t = normalizedEventType(event.eventType);
  switch (chip) {
    case "all": return true;
    case "llm":
      return t.startsWith("llm_") || t === "step_text_complete";
    case "tool":
      return t === "agent_tool_call";
    case "decision":
      return t === "decision_made" || t.startsWith("agent_decide");
    case "checkpoint":
      return t.startsWith("checkpoint_");
    case "lifecycle":
      return t.startsWith("run_") || t.startsWith("step_");
    case "errors":
      return t.includes("fail") || t.includes("rejected") || t.includes("cap_exceeded") || t.includes("expired");
    default:
      return true;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface EventTimelineProps {
  events: RunEvent[];
  isConnected: boolean;
  useFallbackPolling: boolean;
  onReconnect?: () => void;
  className?: string;
}

const FILTER_CHIPS: FilterChip[] = ["all", "llm", "tool", "decision", "checkpoint", "lifecycle", "errors"];

// Estimated row height for the virtualizer — rows expand so we over-estimate slightly.
const ESTIMATED_ROW_HEIGHT = 44;

export function EventTimeline({
  events,
  isConnected,
  useFallbackPolling,
  onReconnect,
  className,
}: EventTimelineProps) {
  const t = useTranslations("workflows");

  const [activeChip, setActiveChip] = useState<FilterChip>("all");
  const [search, setSearch] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  // Filter + search — token_delta excluded upstream (never in events[])
  const filtered = events.filter((ev) => {
    if (!matchesChip(ev, activeChip)) return false;
    if (!search) return true;
    const haystack = `${ev.eventType} ${JSON.stringify(ev.payload)}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 10,
  });

  // Auto-scroll to bottom on new events when enabled.
  useEffect(() => {
    if (!autoScroll || filtered.length === prevCountRef.current) return;
    prevCountRef.current = filtered.length;
    if (filtered.length > 0) {
      virtualizer.scrollToIndex(filtered.length - 1, { behavior: "smooth" });
    }
  }, [filtered.length, autoScroll, virtualizer]);

  // Detect upward scroll to disable auto-scroll.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    setAutoScroll(atBottom);
  }, []);

  const chipLabel = (chip: FilterChip) => t(`run.timeline.filter_${chip}` as Parameters<typeof t>[0], { defaultValue: chip });

  return (
    <div className={cn("flex flex-col gap-2 h-full", className)}>
      {/* Reconnect / fallback banner */}
      {!isConnected && !useFallbackPolling && (
        <SuggestBanner
          tone="warning"
          icon={<Wifi size={14} aria-hidden className="animate-[spin_1.4s_linear_infinite]" />}
          title={t("run.timeline.reconnecting")}
          description={t("run.timeline.reconnecting_desc")}
          actions={
            onReconnect && (
              <button
                type="button"
                onClick={onReconnect}
                className="text-[11px] font-semibold text-[var(--warning)] hover:underline"
              >
                {t("run.timeline.reconnect_now")}
              </button>
            )
          }
        />
      )}
      {useFallbackPolling && (
        <SuggestBanner
          tone="warning"
          icon={<Wifi size={14} aria-hidden />}
          title={t("run.timeline.fallback_polling")}
          description={t("run.timeline.fallback_polling_desc")}
        />
      )}

      {/* Toolbar: filter chips + search */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => setActiveChip(chip)}
              className={cn(
                "px-2.5 py-1 rounded-[var(--radius-full)] text-[11px] font-semibold leading-none",
                "transition-colors duration-[120ms] border",
                activeChip === chip
                  ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                  : "bg-[var(--card)] text-[var(--muted-foreground)] border-[var(--border)] hover:border-[var(--primary)] hover:text-[var(--primary)]"
              )}
            >
              {chipLabel(chip)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 flex-1 min-w-[140px] max-w-[220px] ml-auto">
          <Search size={12} className="text-[var(--muted-foreground)] shrink-0" aria-hidden />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("run.timeline.search_placeholder")}
            className={cn(
              "w-full bg-transparent text-[12px] text-[var(--foreground)]",
              "placeholder:text-[var(--muted-foreground)] outline-none border-0"
            )}
            aria-label={t("run.timeline.search_placeholder")}
          />
        </div>

        {/* Auto-scroll toggle */}
        <button
          type="button"
          onClick={() => setAutoScroll((v) => !v)}
          className={cn(
            "text-[11px] font-medium px-2 py-1 rounded-[var(--radius-full)] border transition-colors duration-[120ms]",
            autoScroll
              ? "bg-[var(--primary-100)] text-[var(--primary-700)] border-[var(--primary)]"
              : "text-[var(--muted-foreground)] border-[var(--border)]"
          )}
          title={t("run.timeline.auto_scroll_toggle")}
        >
          {autoScroll ? t("run.timeline.auto_scroll_on") : t("run.timeline.auto_scroll_off")}
        </button>
      </div>

      {/* Event count */}
      <p className="text-[11px] text-[var(--muted-foreground)]">
        {t("run.timeline.event_count", { count: filtered.length })}
      </p>

      {/* Live region — throttled to 1/sec for screen readers */}
      <div aria-live="polite" aria-atomic="false" className="sr-only" aria-label="Event log updates" />

      {/* Virtualised list */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto custom-scrollbar min-h-0"
        role="log"
        aria-label={t("run.timeline.aria_label")}
      >
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-[12px] text-[var(--muted-foreground)]">
            {t("run.timeline.empty")}
          </p>
        ) : (
          <div
            style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const event = filtered[virtualItem.index];
              const eventType = normalizedEventType(event.eventType);
              const isError =
                eventType.includes("fail") ||
                eventType.includes("rejected") ||
                eventType.includes("expired") ||
                eventType.includes("cap_exceeded");
              return (
                <div
                  key={event.id || virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <EventRow
                    event={event}
                    autoExpand={isError}
                    systemActorLabel={t("run.event.system_actor")}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
